import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, toHandlers as _toHandlers, mergeProps as _mergeProps, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "form-group-child" }
const _hoisted_2 = { for: "password" }
const _hoisted_3 = { class: "t-password-group" }
const _hoisted_4 = ["value", "type", "placeholder"]
const _hoisted_5 = {
  key: 0,
  class: "error-text",
  "data-qe-id": "password-error"
}
const _hoisted_6 = { class: "t-progress-group" }
const _hoisted_7 = ["value"]
const _hoisted_8 = { class: "password-input-title" }
const _hoisted_9 = { "data-qe-id": "list-requirements" }

import { computed, ref, Ref, useAttrs, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Password } from '@/model/Password'
import { validatePassword as _validatePassword, overAllScore as _overAllScore } from '@/lib/passwordRequirements'

interface Props {
  validate?: boolean
  modelValue: Password
  name?: string
  email?: string
  currentPassword?: string
  newPasswordText?: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'PasswordInput',
  props: {
    validate: { type: Boolean },
    modelValue: {},
    name: {},
    email: {},
    currentPassword: {},
    newPasswordText: { type: Boolean }
  },
  emits: ['helpshift', 'update:modelValue', 'reset'],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const { t } = useI18n()
const attrs = useAttrs()

const emit = __emit

const props = __props

const password: Ref<Password> = ref({
  value: '',
  valid: false,
  errorCode: undefined,
  length: false,
  numbers: false,
  specialChars: false,
  lowerUpperCase: false,
  scoreOk: false,
  score: 0,
  sameAsOldPassword: false,
  sameAsUserName: false,
  similarToUserName: false,
  sameAsEmail: false,
})
const enablePasswordValidation = ref(false)
const showPassword = ref(false)
const showPasswordHint = ref(false)
const inputField = ref(null)

watch(
  () => props.modelValue,
  (to: Password) => {
    password.value = to
    // should close after "reset" when submit but not when clear the password by there own
    if (password.value.value.length === 0 && password.value.valid === true) {
      showPasswordHint.value = false
    }
  },
  { deep: true },
)

watch(
  () => props.validate,
  (to: boolean) => {
    enablePasswordValidation.value = to
    password.value = _validatePassword({
      password: password.value.value,
      name: props.name,
      currentPassword: props.currentPassword,
      email: props.email,
    })
    emit('update:modelValue', password.value)
  },
)

watch(
  () => props.name,
  (to: string) => {
    if (password.value.value.length > 0) {
      enablePasswordValidation.value = true
      password.value = _validatePassword({
        password: password.value.value,
        name: to,
        currentPassword: props.currentPassword,
        email: props.email,
      })
      emit('update:modelValue', password.value)
    }
  },
)

watch(
  () => props.email,
  (to: string) => {
    if (password.value.value.length > 0) {
      enablePasswordValidation.value = true
      password.value = _validatePassword({
        password: password.value.value,
        name: props.name,
        currentPassword: props.currentPassword,
        email: to,
      })
      emit('update:modelValue', password.value)
    }
  },
)

watch(
  () => props.currentPassword,
  (to: string) => {
    if (password.value.value.length > 0) {
      enablePasswordValidation.value = true
      password.value = _validatePassword({
        password: password.value.value,
        name: props.name,
        currentPassword: to,
        email: props.email,
      })
      emit('update:modelValue', password.value)
    }
  },
)

const progressUi = computed(() => {
  return {
    color: password.value.valid ? 'good' : password.value.value.length === 0 ? '' : 'bad',
    icon: password.value.valid
      ? 'icfB-faceSmiling'
      : password.value.value.length === 0
      ? 'icfB-faceNeutral'
      : 'icfB-faceFrowling',
  }
})

const overAllScore = computed(() => {
  return _overAllScore(password.value)
})

/**
 * Declare event-handlers for input fields and register handlers defined on the outside (in parent component)
 *
 * @return  {[event:string]: Function}  Map of event-handlers, applied in template via attribute "v-on"
 */
const listeners = computed(() => {
  return {
    ...attrs,
    focus: () => (showPasswordHint.value = true),
    blur: onPasswordBlur,
    input: (event: InputEvent) => validatePasswordOnInput((event.target as HTMLInputElement).value),
  }
})

/**
 * Public method to invoke bluring the actual text-input
 *
 * @return  {void}
 */
const blur = () => {
  inputField.value.blur()
}

const focus = () => {
  inputField.value.focus()
}

const validatePasswordOnInput = (s: string) => {
  // TODO test also with current password on change password form !!
  password.value = _validatePassword({ password: s, name: props.name, email: props.email })
  if (s.length === 0 || password.value.valid) {
    enablePasswordValidation.value = false
    emit('reset')
  }
  emit('update:modelValue', password.value)
}

const onPasswordBlur = () => {
  // if (this.password.value.length === 0) {
  //   this.showPasswordHint = false
  // }
  if (password.value.value.length > 0) {
    password.value = _validatePassword({
      password: password.value.value,
      name: props.name,
      currentPassword: props.currentPassword,
      email: props.email,
    })
    enablePasswordValidation.value = true
  } else {
    enablePasswordValidation.value = false
  }
}

const onHelpshift = (event: MouseEvent) => {
  event.preventDefault()
  emit('helpshift')
}

const reset = () => {
  password.value = {
    value: '',
    valid: false,
    errorCode: undefined,
    length: false,
    numbers: false,
    specialChars: false,
    lowerUpperCase: false,
    scoreOk: false,
    score: 0,
    sameAsOldPassword: false,
    sameAsUserName: false,
    similarToUserName: false,
    sameAsEmail: false,
  }
  showPasswordHint.value = false
}

__expose({
  focus,
  blur,
  reset,
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("label", _hoisted_2, _toDisplayString(_ctx.newPasswordText ? _unref(t)('form.label.passwordNew') : _unref(t)('form.label.password')), 1),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("input", _mergeProps({
        ref_key: "inputField",
        ref: inputField,
        autocomplete: "new-password",
        "data-qe-id": "password",
        class: enablePasswordValidation.value && (!password.value.valid || password.value.errorCode) ? 'error' : '',
        name: "password",
        value: password.value.value,
        type: showPassword.value ? 'text' : 'password'
      }, _toHandlers(listeners.value, true), {
        placeholder: _ctx.newPasswordText ? _unref(t)('form.placeholder.passwordNew') : _unref(t)('form.placeholder.password'),
        maxlength: "100"
      }), null, 16, _hoisted_4),
      _createElementVNode("div", {
        onClick: _cache[0] || (_cache[0] = ($event: any) => (showPassword.value = true)),
        class: "eye-on-icon icf-colorblindOn"
      }),
      _createElementVNode("div", {
        onClick: _cache[1] || (_cache[1] = ($event: any) => (showPassword.value = false)),
        class: "eye-off-icon icf-colorblindOff"
      })
    ]),
    (enablePasswordValidation.value && password.value.errorCode)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(_unref(t)(`form.errors.${password.value.errorCode}`)), 1))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_6, [
      _createElementVNode("div", {
        class: _normalizeClass(["progressCont", progressUi.value.color])
      }, [
        _createElementVNode("progress", {
          class: "progress",
          value: overAllScore.value,
          max: "4",
          "data-qe-id": "progress"
        }, null, 8, _hoisted_7)
      ], 2),
      _createElementVNode("i", {
        class: _normalizeClass([progressUi.value.icon, progressUi.value.color])
      }, null, 2)
    ]),
    _createElementVNode("div", {
      class: _normalizeClass(["t-criteria-group", showPasswordHint.value ? 'show' : ''])
    }, [
      _createElementVNode("strong", _hoisted_8, _toDisplayString(_unref(t)('pfwForm.requirements.title')), 1),
      _createElementVNode("ul", _hoisted_9, [
        _createElementVNode("li", {
          class: _normalizeClass([password.value.length ? 'icf-ok' : 'icf-warning', "required"])
        }, _toDisplayString(_unref(t)('pfwForm.requirements.length')), 3),
        _createElementVNode("li", {
          class: _normalizeClass([password.value.scoreOk ? 'icf-ok' : 'icf-warning', "required"])
        }, _toDisplayString(_unref(t)('pfwForm.requirements.complexity')), 3),
        _createElementVNode("li", {
          class: _normalizeClass([password.value.lowerUpperCase ? 'icf-ok' : 'icf-warning', !password.value.valid ? 'required' : ''])
        }, _toDisplayString(_unref(t)('pfwForm.requirements.capitalisation')), 3),
        _createElementVNode("li", {
          class: _normalizeClass([
            password.value.specialChars || password.value.numbers ? 'icf-ok' : 'icf-warning',
            !password.value.valid ? 'required' : '',
          ])
        }, _toDisplayString(_unref(t)('pfwForm.requirements.characters')), 3)
      ]),
      _createElementVNode("a", {
        tabindex: "-1",
        href: "javascript:void(0)",
        onMousedown: onHelpshift,
        "data-qe-id": "link-passwordhint"
      }, _toDisplayString(_unref(t)('pfwForm.help.link')), 33)
    ], 2)
  ]))
}
}

})